/**
 * OpportunityMap Component
 * Displays opportunities on a Google Map centered on the active ecosystem's
 * `taxonomy.mapCenter` (with a continental-US fallback in platform mode).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import {
    Card,
    Row,
    Col,
    Spinner,
    Alert,
    Form,
    Badge,
    ListGroup,
} from 'react-bootstrap';

import { useOpportunityApi } from '@/shared/services/opportunityApi';
import useEcosystemTaxonomy from '@/shared/hooks/useEcosystemTaxonomy';

import { getTypeColor } from '../utils/opportunityColors';

import '../Opportunities.css';

const MAP_OPTIONS = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
};

const MAP_CONTAINER_STYLE = {
    width: '100%',
    height: '500px',
    borderRadius: '8px',
};

// Map pin SVG path (teardrop shape)
const PIN_PATH =
    'M12 0C7.03 0 3 4.03 3 9c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9z';

export default function OpportunityMap({ filters, filterOptions }) {
    const opportunityApi = useOpportunityApi();
    const navigate = useNavigate();
    const lastTapRef = useRef({ id: null, time: 0 });
    const { mapCenter, mapDefaultZoom, defaultStateCode } =
        useEcosystemTaxonomy();

    const [mapData, setMapData] = useState({ features: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const mapRef = useRef(null);

    // Filters
    const [typeFilter, setTypeFilter] = useState(filters.type || '');
    const [regionFilter, setRegionFilter] = useState(filters.region || '');

    // Load Google Maps
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    });

    // Load map data
    useEffect(() => {
        const loadMapData = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = {};
                if (typeFilter) params.type = typeFilter;
                if (regionFilter) params.region = regionFilter;

                const result = await opportunityApi.fetchMapData(params);
                setMapData(result);
            } catch (err) {
                console.error('Failed to load map data:', err);
                setError('Failed to load map data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadMapData();
    }, [opportunityApi, typeFilter, regionFilter]);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    // Build a pin-shaped marker icon colored by opportunity type
    const getMarkerIcon = (type) => {
        const color = getTypeColor(type);
        return {
            path: PIN_PATH,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
            scale: 1.6,
            anchor: window.google
                ? new window.google.maps.Point(12, 24)
                : undefined,
        };
    };

    const handleMarkerClick = (feature) => {
        setSelectedMarker(feature);
    };

    const handleListClick = (e, feature) => {
        e.preventDefault();
        const now = Date.now();
        const tap = lastTapRef.current;

        if (tap.id === feature.properties.id && now - tap.time < 400) {
            // Double-tap: navigate to detail page
            navigate(`/opportunities/${feature.properties.id}`);
            lastTapRef.current = { id: null, time: 0 };
        } else {
            // Single tap: zoom to pin
            lastTapRef.current = { id: feature.properties.id, time: now };
            handleListHover(feature);
        }
    };

    const handleListHover = (feature) => {
        setSelectedMarker(feature);
        const [lng, lat] = feature.geometry?.coordinates || [];
        if (lat && lng && mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(11);
        }
    };

    if (loadError) {
        return (
            <Alert variant="danger">
                Failed to load Google Maps. Please check your API key
                configuration.
            </Alert>
        );
    }

    if (!isLoaded) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading map...</p>
            </div>
        );
    }

    return (
        <div className="opportunity-map">
            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3 align-items-end">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Opportunity Type
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={typeFilter}
                                    onChange={(e) =>
                                        setTypeFilter(e.target.value)
                                    }
                                >
                                    <option value="">All Types</option>
                                    {filterOptions.opportunityTypes?.map(
                                        (type) => (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        )
                                    )}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Region
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={regionFilter}
                                    onChange={(e) =>
                                        setRegionFilter(e.target.value)
                                    }
                                >
                                    <option value="">All Regions</option>
                                    {filterOptions.regions?.map((region) => (
                                        <option key={region} value={region}>
                                            {region}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <p className="small text-muted mb-0">
                                {mapData.count || 0} opportunities with
                                locations
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            <Row>
                {/* Map */}
                <Col lg={8} className="mb-4 mb-lg-0">
                    <div className="opportunity-map-container shadow-sm position-relative">
                        <GoogleMap
                            mapContainerStyle={MAP_CONTAINER_STYLE}
                            center={mapCenter}
                            zoom={mapDefaultZoom}
                            options={MAP_OPTIONS}
                            onLoad={onMapLoad}
                            onUnmount={onMapUnmount}
                            onClick={() => setSelectedMarker(null)}
                        >
                            {mapData.features?.map((feature) => {
                                const { id, type } = feature.properties;
                                const [lng, lat] =
                                    feature.geometry?.coordinates || [];

                                if (!lat || !lng) return null;

                                return (
                                    <Marker
                                        key={id}
                                        position={{ lat, lng }}
                                        icon={getMarkerIcon(type)}
                                        onClick={() =>
                                            handleMarkerClick(feature)
                                        }
                                    />
                                );
                            })}
                        </GoogleMap>

                        {/* Custom detail overlay (replaces Google InfoWindow) */}
                        {selectedMarker && (
                            <div className="map-detail-overlay">
                                <button
                                    type="button"
                                    className="map-detail-overlay-close"
                                    onClick={() => setSelectedMarker(null)}
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                                <h6 className="map-info-window-title mb-1">
                                    {selectedMarker.properties.title}
                                </h6>
                                <p className="small text-muted mb-2">
                                    {selectedMarker.properties.organization}
                                </p>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    {/* CSS custom property for dynamic type color -- must be inline */}
                                    <Badge
                                        bg=""
                                        className="type-color-badge"
                                        style={{
                                            '--type-color': getTypeColor(
                                                selectedMarker.properties.type
                                            ),
                                        }}
                                    >
                                        {selectedMarker.properties.type}
                                    </Badge>
                                    {selectedMarker.properties.city && (
                                        <span className="small text-muted">
                                            {selectedMarker.properties.city}
                                            {defaultStateCode
                                                ? `, ${defaultStateCode}`
                                                : ''}
                                        </span>
                                    )}
                                </div>
                                <Link
                                    to={`/opportunities/${selectedMarker.properties.id}`}
                                    className="btn btn-sm btn-primary w-100"
                                >
                                    View Details
                                </Link>
                            </div>
                        )}
                    </div>
                </Col>

                {/* Side List */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white">
                            <strong>Nearby Opportunities</strong>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner
                                        animation="border"
                                        variant="primary"
                                        size="sm"
                                    />
                                </div>
                            ) : mapData.features?.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <small>
                                        No opportunities with locations found.
                                    </small>
                                </div>
                            ) : (
                                <ListGroup
                                    variant="flush"
                                    className="map-side-list"
                                >
                                    {mapData.features
                                        ?.slice(0, 20)
                                        .map((feature) => (
                                            <ListGroup.Item
                                                key={feature.properties.id}
                                                action
                                                className="py-3 map-list-item"
                                                onClick={(e) =>
                                                    handleListClick(e, feature)
                                                }
                                                onMouseEnter={() =>
                                                    handleListHover(feature)
                                                }
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <h6 className="mb-1 text-primary small">
                                                            {
                                                                feature
                                                                    .properties
                                                                    .title
                                                            }
                                                        </h6>
                                                        <small className="text-muted">
                                                            {
                                                                feature
                                                                    .properties
                                                                    .organization
                                                            }
                                                        </small>
                                                        <div className="mt-1">
                                                            {/* CSS custom property for dynamic type color -- must be inline */}
                                                            <Badge
                                                                bg=""
                                                                className="type-color-badge small"
                                                                style={{
                                                                    '--type-color':
                                                                        getTypeColor(
                                                                            feature
                                                                                .properties
                                                                                .type
                                                                        ),
                                                                }}
                                                            >
                                                                {
                                                                    feature
                                                                        .properties
                                                                        .type
                                                                }
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    {(feature.properties.city ||
                                                        defaultStateCode) && (
                                                        <Badge
                                                            bg="light"
                                                            text="dark"
                                                            className="small"
                                                        >
                                                            {feature.properties
                                                                .city ||
                                                                defaultStateCode}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Legend - shows types actually present in map data */}
            {mapData.features?.length > 0 && (
                <Card className="border-0 shadow-sm mt-4">
                    <Card.Body className="py-2">
                        <div className="d-flex flex-wrap gap-3 justify-content-center small">
                            {[
                                ...new Set(
                                    mapData.features.map(
                                        (f) => f.properties.type
                                    )
                                ),
                            ]
                                .sort()
                                .map((type) => (
                                    <span
                                        key={type}
                                        className="d-flex align-items-center gap-1"
                                    >
                                        {/* CSS custom property for dynamic dot color -- must be inline */}
                                        <span
                                            className="map-legend-dot"
                                            style={{
                                                '--dot-color':
                                                    getTypeColor(type),
                                            }}
                                        />
                                        {type}
                                    </span>
                                ))}
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

OpportunityMap.propTypes = {
    filters: PropTypes.shape({
        type: PropTypes.string,
        region: PropTypes.string,
    }),
    filterOptions: PropTypes.shape({
        opportunityTypes: PropTypes.arrayOf(PropTypes.string),
        regions: PropTypes.arrayOf(PropTypes.string),
    }),
};

OpportunityMap.defaultProps = {
    filters: {},
    filterOptions: {},
};
